import { inject, Injectable, signal } from "@angular/core";
import { DefaultService } from "@core/services/default.service";
import {
  ICompany,
  ICompanyData,
  ICompanyInsert,
  ICompanyUpdate,
} from "../models/companies.interface";
import {
  ColDef,
  ColGroupDef,
  RowClassParams,
  RowStyle,
} from "ag-grid-community";
import { StatusComponent } from "@shared/datatable/actions/Status.component";
import { ResidentActionsComponent } from "@shared/datatable/actions/resident-actions.component";
import { Router } from "@angular/router";
import { CabFileInputService } from "@shared/form/service/cab-file-input.service";
import { CompaniesModalEditComponent } from "@modules/management/pages/companies/companies-modal-edit/companies-modal-edit.component";
import { DatePipe } from "@angular/common";
import { CompaniesContractEndDateComponent } from "@shared/datatable/actions/companies-contract-end-date.component";
import { ERoless } from "@core/enums/Roles.enum";
import { checkContractEnd, hasContractEnded } from "@shared/utils/date";

@Injectable({
  providedIn: "root",
})
export class CompaniesService extends DefaultService<
  ICompanyData,
  ICompanyInsert,
  ICompanyUpdate
> {
  getServiceKey(): string {
    return "CompaniesService";
  }

  isPending = signal(false);

  constructor(
    private _router: Router,
    private datePipe: DatePipe
  ) {
    super("user-service/companies");
  }

  user = this._localStorageService.getData("user");
  listOfAllowedRoles = [
    ERoless.REPUBLIC_ADMIN,
    ERoless.REGION_ADMIN,
    ERoless.SUPER_ADMIN,
  ];

  _cabFileInputService = inject(CabFileInputService);

  columnDefs: (ColDef | ColGroupDef)[] = [
    {
      headerName: "COMPANY.TABLE_HEADERS.ID",
      filter: false,
      field: "id",
      pinned: "left",
      minWidth: 70,
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.NAME",
      field: "name",
      pinned: "left",
      minWidth: 300,
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.TYPE",
      field: "ownershipTypeName",
      minWidth: 230,
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.REGION",
      field: "districtName",
      minWidth: 150,
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.COMPANY_ACCOUNT",
      field: "companyAccountNumber",
      minWidth: 200,
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.TRANSIT_NUMBER",
      field: "transitAccountNumber",
      minWidth: 200,
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.TREASURY_ACC_NUMBER",
      field: "treasuryAccountNumber",
      minWidth: 200,
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.TRANSIT_ACC_MFO",
      field: "transitAccMfo",
      minWidth: 200,
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.DIRECTOR_FIO",
      field: "directorFio",
      minWidth: 250,
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.PHONE_NUMBER",
      field: "phone",
      minWidth: 150,
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.INN",
      field: "inn",
      minWidth: 150,
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.TURNOVER",
      field: "bankMfo",
      minWidth: 100,
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.BANK_NAME",
      field: "bankName",
      minWidth: 300,
    },
    {
      headerName: "COMPANY.DXSH_PERCENTAGE",
      field: "dxshPercentage",
      minWidth: 150,
    },
    {
      headerName: "MENU.DXSH.CONTRACTS",
      autoHeaderHeight: true,
      children: [
        {
          headerName: "COMPANY.TABLE_HEADERS.CONTRACT_NUMBER",
          field: "contractNo",
          minWidth: 150,
        },
        {
          headerName: "COMPANY.TABLE_HEADERS.CONTRACT_START_DATE",
          field: "contractStartDate",
          valueFormatter: (params) => {
            return params.value
              ? <string>this.datePipe.transform(params.value, "dd.MM.yyyy")
              : "";
          },
          minWidth: 140,
        },
        {
          headerName: "COMPANY.TABLE_HEADERS.CONTRACT_END_DATE",
          field: "contractEndDate",
          cellRenderer: CompaniesContractEndDateComponent,
          minWidth: 140,
        },
        {
          headerName: "COMPANY.TABLE_HEADERS.CONTRACT_CREATED_AT",
          field: "contractCreatedAt",
          minWidth: 200,
          cellRenderer: CompaniesContractEndDateComponent,
        },
        {
          headerName: "COMPANY.TABLE_HEADERS.CONTRACT_CREATED_BY_NAME",
          field: "contractCreatedByName",
          minWidth: 200,
        },
      ],
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.EMAIL",
      field: "email",
      minWidth: 200,
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.ADDRESS",
      field: "address",
      minWidth: 300,
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.STATUS",
      field: "isActive",
      cellRenderer: StatusComponent,
      minWidth: 100,
    },
    {
      headerName: "COMPANY.TABLE_HEADERS.COMMENT",
      field: "description",
      minWidth: 300,
    },
    {
      field: "actions",
      headerName: "TOOLS.RESIDENTS.TABLE_HEADERS.ACTION",
      headerClass: "header-cell-center",
      cellRenderer: ResidentActionsComponent<ICompany>,
      maxWidth: 150,
      minWidth: 120,
      cellRendererParams: {
        handleEdit: this.listOfAllowedRoles.includes(
          this.user?.role?.name || ""
        )
          ? this.onEdit.bind(this)
          : null,
        handleGoLink: this.onGoLink.bind(this),
      },
      suppressNavigable: true,
      cellClass: "no-border",
      pinned: "right",
    },
  ];

  onEdit(data: ICompany) {
    this.selectedRow.set(data);
    this._modalService.open({
      component: CompaniesModalEditComponent,
      title: "COMPANY.MODAL.EDIT_TITLE",
      modalOptions: {
        size: "xl",
      },
    });
  }

  onGoLink(data: ICompany) {
    this._router.navigateByUrl(`company/${data.id}`);
  }

  getDownloadExcel() {
    this.isPending.set(true);
    this._cabFileInputService.downloadFile(
      this._translateService.instant("MENU.MANAGEMENT.COMPANIES"),
      `${this._api}/excel`,
      {},
      () => {
        this.isPending.set(false);
      },
      () => {
        this.isPending.set(false);
      }
    );
  }

  override getRowStyle = (params: RowClassParams): RowStyle | undefined => {
    if (params.data === undefined) {
      return { opacity: 0 };
    }
    if (params.data && hasContractEnded(params.data.contractEndDate)) {
      return {
        background: "rgba(255, 0, 21, 0.5) !important",
      };
    }
    if (params.data && checkContractEnd(params.data.contractEndDate)) {
      return {
        background: "#fcd33d !important",
      };
    } else {
      return {};
    }
  };
}
